import {
  BRAND,
  daysText,
  fmtNum,
  fmtSize,
  lei,
  numInput,
  parseNum,
  PUBLIC_OFFER_STATE_TEXT,
  publicOfferTotal,
  roDate,
  roDateTime,
  type PublicOffer,
  type PublicOfferInput,
  siteTermOf,
  siteText,
  t,
} from '@procumi/shared';
import { useEffect, useMemo, useState } from 'react';
import { syncLang } from '../lib/i18n';
import { Loading } from '../components/StateBox';
import { SupplierProfileCard } from '../components/SupplierProfileCard';
import { api, errMsg } from '../lib/api';
import { DateInput } from '../components/DateInput';
import { rich } from '../lib/rich';

/**
 * Pagina furnizorului, din linkul primit pe mail (Echipă, Companie). Fără cont:
 * vede cererea, trece prețurile și condițiile, trimite. Oferta ajunge direct în
 * tabelul comparativ al cumpărătorului și se poate schimba cât timp cererea e
 * deschisă. Ce a scris rămâne în browser până trimite, ca o pagină reîncărcată
 * să nu-i piardă munca.
 */

interface Draft {
  prices: Record<string, string>;
  alts: Record<string, string>;
  altOpen: Record<string, boolean>;
  deliveryDays: string;
  paymentDays: string;
  validUntil: string;
  transport: '' | 'included' | 'extra';
  transportCost: string;
  notes: string;
}

const emptyDraft: Draft = {
  prices: {},
  alts: {},
  altOpen: {},
  deliveryDays: '',
  paymentDays: '',
  validUntil: '',
  transport: '',
  transportCost: '',
  notes: '',
};

function draftFrom(o: PublicOfferInput | null): Draft {
  if (!o) return emptyDraft;
  const str = (n: number | null) => (n == null ? '' : numInput(n));
  return {
    prices: Object.fromEntries(Object.entries(o.prices).map(([k, v]) => [k, numInput(v)])),
    alts: o.alts,
    altOpen: Object.fromEntries(Object.keys(o.alts).map((k) => [k, true])),
    deliveryDays: str(o.deliveryDays),
    paymentDays: str(o.paymentDays),
    validUntil: o.validUntil ?? '',
    transport: o.transport,
    transportCost: str(o.transportCost),
    notes: o.notes,
  };
}

const storeKey = (token: string) => `procumi:oferta:${token}`;
function loadDraft(token: string): Draft | null {
  try {
    const raw = localStorage.getItem(storeKey(token));
    return raw ? { ...emptyDraft, ...(JSON.parse(raw) as Partial<Draft>) } : null;
  } catch {
    return null;
  }
}
function saveDraft(token: string, d: Draft | null): void {
  try {
    if (d) localStorage.setItem(storeKey(token), JSON.stringify(d));
    else localStorage.removeItem(storeKey(token));
  } catch {
    /* fără stocare: ciorna se pierde la reîncărcare, atât */
  }
}

/** Numărul din casetă, sau null dacă e gol; NaN dacă e scris greșit. */
const num = (v: string): number | null => (v.trim() ? (parseNum(v) ?? Number.NaN) : null);

/** „într-o zi”, „în 3 zile”, „imediat”: nu „în o zi”. */
function inDays(d: number): string {
  if (d === 0) return 'imediat';
  const days = daysText(d);
  return days.startsWith('o ') ? t('într-{0}', days) : t('în {0}', days);
}

export function PublicOfferPage({ token }: { token: string }) {
  const [data, setData] = useState<PublicOffer | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editing, setEditing] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    document.title = t('Ofertă — {0}', BRAND.name);
    api
      .publicOffer(token)
      .then((d) => {
        // pagina furnizorului, în limba lui și cu cuvântul firmei care cumpără
        syncLang(d.locale ?? 'ro', d.siteTerm, d.currency, d.country);
        setData(d);
        setDraft(loadDraft(token) ?? draftFrom(d.offer));
        setEditing(!d.submittedAt);
      })
      .catch((e) => setLoadErr(errMsg(e)));
  }, [token]);

  const patch = (p: Partial<Draft>) =>
    setDraft((d) => {
      const next = { ...d, ...p };
      saveDraft(token, next);
      return next;
    });

  const items = data?.items ?? [];
  const priced = useMemo(() => {
    const out: Record<string, number> = {};
    for (const it of items) {
      const n = num(draft.prices[it.id] ?? '');
      if (n != null && Number.isFinite(n) && n > 0) out[it.id] = n;
    }
    return out;
  }, [items, draft.prices]);
  const total = publicOfferTotal(items, priced);
  const count = Object.keys(priced).length;

  const submit = async () => {
    setErr('');
    const bad = items.find((it) => {
      const n = num(draft.prices[it.id] ?? '');
      return n != null && !(Number.isFinite(n) && n > 0);
    });
    if (bad) return setErr(t('Prețul la „{0}” nu e un număr bun.', bad.desc));
    if (!count) return setErr(t('Treceți prețul la cel puțin o poziție.'));
    const days = (v: string, what: string) => {
      const n = num(v);
      if (n != null && !(Number.isInteger(n) && n >= 0))
        throw new Error(t('{0}: scrieți un număr de zile.', what));
      return n;
    };
    let body: PublicOfferInput;
    try {
      const cost = draft.transport === 'extra' ? num(draft.transportCost) : null;
      if (cost != null && !(Number.isFinite(cost) && cost >= 0)) {
        throw new Error(t('Costul transportului nu e un număr bun.'));
      }
      body = {
        prices: priced,
        alts: Object.fromEntries(
          Object.entries(draft.alts).filter(([k, v]) => draft.altOpen[k] && v.trim() && priced[k]),
        ),
        deliveryDays: days(draft.deliveryDays, t('Livrarea')),
        paymentDays: days(draft.paymentDays, t('Plata')),
        validUntil: draft.validUntil || null,
        transport: draft.transport,
        transportCost: cost,
        notes: draft.notes.trim(),
      };
    } catch (e) {
      return setErr(errMsg(e));
    }
    setBusy(true);
    try {
      const d = await api.submitPublicOffer(token, body);
      saveDraft(token, null);
      setData(d);
      setDraft(draftFrom(d.offer));
      setEditing(false);
      window.scrollTo(0, 0);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (loadErr) {
    return (
      <Shell>
        <div className="sheet pad so-msg">
          <h1>{t('Linkul nu se deschide')}</h1>
          <p>{loadErr}</p>
        </div>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <Loading />
      </Shell>
    );
  }

  const contact = data.buyer.email ? <a href={`mailto:${data.buyer.email}`}>{data.buyer.email}</a> : null;

  if (data.state !== 'open') {
    return (
      <Shell company={data.company}>
        <div className="sheet pad so-msg">
          <p className="so-kicker">
            {data.company} · {data.title}
          </p>
          <h1>{data.state === 'closed' ? t('Cererea s-a încheiat') : t('Oferta nu se mai trimite aici')}</h1>
          <p>{PUBLIC_OFFER_STATE_TEXT[data.state]}</p>
          {contact ? (
            <p className="hint">
              {data.buyer.name}: {contact}
            </p>
          ) : null}
        </div>
      </Shell>
    );
  }

  const facts = (
    <dl className="so-facts">
      <div>
        <dt>{t('Livrare la')}</dt>
        <dd>{data.deliveryTo}</dd>
      </div>
      <div>
        <dt>{t('Oferta până pe')}</dt>
        <dd>{roDate(data.deadline)}</dd>
      </div>
      {data.neededBy ? (
        <div>
          {/* furnizorul n-are cont: cuvântul e al firmei care cumpără */}
          <dt>{siteText(t('Necesar pe șantier'), siteTermOf(data.siteTerm))}</dt>
          <dd>{roDate(data.neededBy)}</dd>
        </div>
      ) : null}
    </dl>
  );

  if (!editing && data.offer) {
    const o = data.offer;
    return (
      <Shell company={data.company}>
        <div className="sheet pad so-done">
          <span className="so-check" aria-hidden="true">
            ✓
          </span>
          <h1>{t('Oferta a ajuns la {0}', data.buyer.name)}</h1>
          <p>
            {t(
              '{0}E deja în tabelul comparativ al firmei {1}. O puteți schimba cât timp cererea e deschisă.',
              data.submittedAt ? t('Trimisă pe {0}. ', roDateTime(data.submittedAt)) : '',
              data.company,
            )}
          </p>
          <div className="so-sum">
            <div>
              <span>{t('Poziții cu preț')}</span>
              <b>{t('{0} din {1}', Object.keys(o.prices).length, items.length)}</b>
            </div>
            <div>
              <span>{t('Total fără TVA')}</span>
              <b>{lei(publicOfferTotal(items, o.prices))}</b>
            </div>
            {o.deliveryDays != null ? (
              <div>
                <span>{t('Livrare')}</span>
                <b>{inDays(o.deliveryDays)}</b>
              </div>
            ) : null}
            {o.paymentDays != null ? (
              <div>
                <span>{t('Plata')}</span>
                <b>{o.paymentDays === 0 ? t('la livrare') : `la ${daysText(o.paymentDays)}`}</b>
              </div>
            ) : null}
          </div>
          <div className="so-done-acts">
            <button className="btn" onClick={() => setEditing(true)}>
              {t('Schimbă oferta')}
            </button>
          </div>
          {contact ? (
            <p className="hint">
              {t('Întrebări despre cerere: {0},', data.buyer.name)} {contact}
            </p>
          ) : null}
        </div>
        {/* datele firmei vin de la ANAF după CUI, deci doar la cumpărătorii din România */}
        {data.country === 'RO' ? <SupplierProfileCard token={token} data={data} onSaved={setData} /> : null}
      </Shell>
    );
  }

  return (
    <Shell company={data.company}>
      <header className="so-head">
        <p className="so-kicker">{t('Cerere de ofertă de la {0}', data.company)}</p>
        <h1>{data.title}</h1>
        <p className="so-lede">
          {t(
            'Bună ziua, {0}. Treceți prețurile unitare, în lei, fără TVA, la pozițiile pe care le aveți. Oferta ajunge direct la {1}.',
            data.supplier,
            data.buyer.name,
          )}
        </p>
        {facts}
        {data.note ? <p className="so-note">{data.note}</p> : null}
        {data.files.length ? (
          <div className="so-plans" id="planuri">
            <b>{t('Planuri și documente')}</b>
            <ul>
              {data.files.map((f) => (
                <li key={f.id}>
                  <a href={f.url} download>
                    {f.filename}
                  </a>
                  <span>{fmtSize(f.size)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </header>

      <section className="sheet so-items" aria-labelledby="so-items-h">
        <h2 id="so-items-h">{t('Prețuri')}</h2>
        <ol className="so-list">
          {items.map((it, i) => {
            const v = draft.prices[it.id] ?? '';
            const n = num(v);
            const ok = n != null && Number.isFinite(n) && n > 0;
            return (
              <li key={it.id} className="so-item">
                <div className="so-what">
                  <span className="so-no">{i + 1}</span>
                  <div>
                    <div className="so-desc">{it.desc}</div>
                    <div className="so-qty">
                      {fmtNum(it.qty)} {it.unit}
                      {it.rentDays != null
                        ? t(' · chirie {0}; prețul pe unitate, pentru toată perioada', daysText(it.rentDays))
                        : ''}
                    </div>
                  </div>
                </div>
                <div className="so-price">
                  <label className="so-in">
                    <span className="sr-only">{t('Preț unitar pentru {0}', it.desc)}</span>
                    <input
                      className="inp"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder={it.rentDays != null ? t('Chirie / unitate') : t('Preț / unitate')}
                      value={v}
                      aria-invalid={v.trim() !== '' && !ok}
                      onChange={(e) => patch({ prices: { ...draft.prices, [it.id]: e.target.value } })}
                    />
                    <span className="so-unit">{t('lei / {0}', it.unit)}</span>
                  </label>
                  <span className="so-line">{ok ? lei(n * it.qty) : ' '}</span>
                </div>
                {draft.altOpen[it.id] ? (
                  <label className="f so-alt">
                    {t('Produsul propus în loc')}
                    <input
                      className="inp"
                      maxLength={200}
                      placeholder={t('ex. echivalent Baumit în loc de Knauf')}
                      value={draft.alts[it.id] ?? ''}
                      onChange={(e) => patch({ alts: { ...draft.alts, [it.id]: e.target.value } })}
                    />
                  </label>
                ) : (
                  <button
                    type="button"
                    className="link so-alt-btn"
                    onClick={() => patch({ altOpen: { ...draft.altOpen, [it.id]: true } })}
                  >
                    {t('Propun alt produs')}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
        <p className="hint so-skip">{t('Ce nu aveți, lăsați gol.')}</p>
      </section>

      <section className="sheet pad so-terms" aria-labelledby="so-terms-h">
        <h2 id="so-terms-h">{t('Condiții')}</h2>
        <div className="so-grid">
          <label className="f">
            {t('Livrare în (zile)')}
            <input
              className="inp"
              inputMode="numeric"
              placeholder={t('ex. 3')}
              value={draft.deliveryDays}
              onChange={(e) => patch({ deliveryDays: e.target.value })}
            />
          </label>
          <label className="f">
            {t('Plata la (zile)')}
            <input
              className="inp"
              inputMode="numeric"
              placeholder={t('ex. 30')}
              value={draft.paymentDays}
              onChange={(e) => patch({ paymentDays: e.target.value })}
            />
          </label>
          <label className="f">
            {t('Oferta valabilă până pe')}
            <DateInput value={draft.validUntil} onChange={(v) => patch({ validUntil: v })} />
          </label>
        </div>
        <fieldset className="so-transport">
          <legend>{t('Transportul')}</legend>
          <div className="so-seg">
            {(
              [
                ['included', t('Inclus în prețuri')],
                ['extra', t('Separat')],
                ['', t('Nu știu încă')],
              ] as const
            ).map(([k, label]) => (
              <label key={k || 'none'} className={draft.transport === k ? 'on' : ''}>
                <input
                  type="radio"
                  name="so-transport"
                  checked={draft.transport === k}
                  onChange={() => patch({ transport: k })}
                />
                {label}
              </label>
            ))}
          </div>
          {draft.transport === 'extra' ? (
            <label className="f so-cost">
              {t('Costul transportului, pentru toată comanda (lei fără TVA)')}
              <input
                className="inp"
                inputMode="decimal"
                placeholder={t('gol dacă nu se știe')}
                value={draft.transportCost}
                onChange={(e) => patch({ transportCost: e.target.value })}
              />
            </label>
          ) : null}
        </fieldset>
        <label className="f">
          {t('Mențiuni (opțional)')}
          <textarea
            className="inp"
            rows={3}
            maxLength={1000}
            placeholder={t('ex. Prețurile includ descărcarea cu macaraua')}
            value={draft.notes}
            onChange={(e) => patch({ notes: e.target.value })}
          />
        </label>
      </section>

      <div className="so-bar">
        <div className="so-total">
          <span>{t('{0} din {1} poziții · fără TVA', count, items.length)}</span>
          <b>{lei(total)}</b>
        </div>
        {err ? (
          <div className="auth-err so-err" role="alert">
            {err}
          </div>
        ) : null}
        <div className="so-bar-acts">
          {data.submittedAt ? (
            <button className="btn" disabled={busy} onClick={() => setEditing(false)}>
              {t('Renunță')}
            </button>
          ) : null}
          <button className="btn primary" disabled={busy} onClick={() => void submit()}>
            {busy ? t('Se trimite…') : data.submittedAt ? t('Trimite oferta schimbată') : t('Trimite oferta')}
          </button>
        </div>
      </div>

      {contact ? (
        <p className="hint so-ask">
          {t('Întrebări despre cerere: {0},', data.buyer.name)} {contact}
        </p>
      ) : null}
    </Shell>
  );
}

function Shell({ company, children }: { company?: string; children: React.ReactNode }) {
  return (
    <div className="so">
      <main className="so-main">{children}</main>
      {/* Fiecare furnizor care răspunde vede Procumi: și el cumpără de la alții (2026-10-27). */}
      <aside className="so-promo">
        <p>
          {rich(
            '<b>Și firma dumneavoastră cere oferte de la furnizori?</b> Cu {0} trimiteți aceeași cerere tuturor și primiți ofertele într-un singur tabel, comparate pe fiecare poziție.',
            BRAND.name,
          )}
        </p>
        <a className="btn" href={`${BRAND.site}/?utm_source=oferta-furnizor`} target="_blank" rel="noopener">
          {t('Încercați gratuit 14 zile')}
        </a>
      </aside>
      <footer className="so-foot">
        <a href={BRAND.site} target="_blank" rel="noopener">
          <img src="/brand/procumi-lockup.svg" alt={BRAND.name} />
        </a>
        <span>
          {t(
            '{0}Nu aveți nevoie de cont.',
            company ? t('Pagină trimisă prin {0} în numele {1}. ', BRAND.name, company) : '',
          )}
          <a href={BRAND.site} target="_blank" rel="noopener">
            {t('Ce este {0}?', BRAND.name)}
          </a>
        </span>
      </footer>
    </div>
  );
}
